import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Plus, Pencil, Trash2, KeyRound, ExternalLink, Copy, BookOpen, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const PROVIDER_TYPES = [
    { value: 'bubble', label: 'Bubble.io', color: '#0052E1' },
    { value: 'google', label: 'Google', color: '#EA4335' },
    { value: 'github', label: 'GitHub', color: '#24292E' },
    { value: 'microsoft', label: 'Microsoft', color: '#0078D4' },
    { value: 'generic', label: 'Genérico OAuth2', color: '#dc2626' },
];

const blankProvider = () => ({
    name: '',
    provider_type: 'generic',
    enabled: true,
    client_id: '',
    client_secret: '',
    auth_url: '',
    token_url: '',
    userinfo_url: '',
    scopes: 'openid profile email',
    redirect_uri: '',
    button_label: '',
    button_color: '#dc2626',
    instructions: '',
});

export default function AdminOAuthSSO() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // {id?: string, ...form}
    const [deleteId, setDeleteId] = useState(null);
    const [saving, setSaving] = useState(false);

    const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
    const defaultCallback = `${backendUrl}/api/oauth/callback`;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/oauth-providers');
            setProviders(data);
        } catch (e) { toast.error('Error cargando proveedores'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startNew = () => setEditing({ ...blankProvider(), redirect_uri: defaultCallback });
    const startEdit = (p) => setEditing({ ...p, client_secret: '' /* input empty; server keeps existing if empty */ });

    const submit = async () => {
        if (!editing) return;
        if (!editing.name || !editing.auth_url || !editing.token_url) {
            toast.error('Faltan campos requeridos');
            return;
        }
        setSaving(true);
        try {
            const body = { ...editing };
            if (!body.client_secret) delete body.client_secret;
            if (editing.id) {
                await api.put(`/admin/oauth-providers/${editing.id}`, body);
                toast.success('Proveedor actualizado');
            } else {
                await api.post('/admin/oauth-providers', body);
                toast.success('Proveedor creado');
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando'); }
        finally { setSaving(false); }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/admin/oauth-providers/${deleteId}`);
            toast.success('Proveedor eliminado');
            setDeleteId(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const copyCallback = () => {
        navigator.clipboard.writeText(defaultCallback);
        toast.success('Callback URL copiada');
    };

    return (
        <div className="space-y-6" data-testid="admin-oauth-sso-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-red-600" />OAuth SSO</h1>
                    <p className="text-sm text-muted-foreground">Configura Single Sign-On con proveedores externos. Bubble.io es el proveedor principal.</p>
                </div>
                <Button onClick={startNew} className="bg-red-600 hover:bg-red-700" data-testid="new-provider-btn">
                    <Plus className="h-4 w-4 mr-1" />Nuevo proveedor
                </Button>
            </div>

            {/* Instructions card */}
            <Alert className="border-red-200 bg-red-50">
                <BookOpen className="h-4 w-4" />
                <AlertTitle>Instrucciones de uso</AlertTitle>
                <AlertDescription className="text-sm space-y-1">
                    <div>1. Registra la app en tu proveedor (ej. Bubble.io) usando el flujo OAuth 2.0 Authorization Code.</div>
                    <div>2. Copia el <b>Client ID</b> y <b>Client Secret</b> que te dé el proveedor.</div>
                    <div>3. Pega el Callback URL en la configuración del proveedor:
                        <code className="ml-1 px-2 py-0.5 bg-white border rounded text-xs font-mono">{defaultCallback}</code>
                        <Button variant="ghost" size="sm" className="ml-1 h-6 px-1" onClick={copyCallback}><Copy className="h-3 w-3" /></Button>
                    </div>
                    <div>4. Ajusta <b>Auth URL</b>, <b>Token URL</b> y <b>Scopes</b> según la documentación del proveedor.</div>
                    <div>5. Activa el proveedor con el switch <i>Habilitado</i> y prueba desde el login público.</div>
                </AlertDescription>
            </Alert>

            {/* Providers list */}
            {loading ? (
                <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : providers.length === 0 ? (
                <div className="rounded-2xl border border-dashed p-10 text-center bg-card">
                    <KeyRound className="h-10 w-10 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mt-3">Aún no hay proveedores configurados.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="providers-list">
                    {providers.map(p => {
                        const type = PROVIDER_TYPES.find(t => t.value === p.provider_type) || PROVIDER_TYPES[4];
                        return (
                            <div key={p.id} className="rounded-2xl border bg-card p-4" data-testid={`provider-${p.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                                            style={{ background: p.button_color || type.color }}>
                                            {p.name[0]?.toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold flex items-center gap-2">
                                                {p.name}
                                                {p.enabled ? <Badge className="bg-emerald-600">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}
                                            </div>
                                            <div className="text-xs text-muted-foreground">{type.label}</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => startEdit(p)} data-testid={`edit-provider-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="sm" onClick={() => setDeleteId(p.id)} data-testid={`delete-provider-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1 text-xs">
                                    <div className="flex gap-2"><span className="text-muted-foreground w-24 shrink-0">Client ID:</span><span className="font-mono truncate">{p.client_id || '—'}</span></div>
                                    <div className="flex gap-2"><span className="text-muted-foreground w-24 shrink-0">Secret:</span><span>{p.has_client_secret ? '✅ Configurado' : '❌ No configurado'}</span></div>
                                    <div className="flex gap-2"><span className="text-muted-foreground w-24 shrink-0">Auth URL:</span><a href={p.auth_url} target="_blank" rel="noreferrer" className="text-red-600 hover:underline truncate flex items-center gap-1">{p.auth_url}<ExternalLink className="h-3 w-3 shrink-0" /></a></div>
                                    <div className="flex gap-2"><span className="text-muted-foreground w-24 shrink-0">Scopes:</span><span className="font-mono truncate">{p.scopes}</span></div>
                                </div>
                                {p.instructions && (
                                    <details className="mt-2">
                                        <summary className="text-xs text-red-600 cursor-pointer hover:underline">Instrucciones específicas</summary>
                                        <pre className="text-[11px] whitespace-pre-wrap mt-1 bg-neutral-50 p-2 rounded border">{p.instructions}</pre>
                                    </details>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Editor Dialog */}
            <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="provider-editor-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? 'Editar proveedor' : 'Nuevo proveedor OAuth 2.0'}</DialogTitle>
                        <DialogDescription>Configuración estándar de Authorization Code Flow.</DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Nombre *</Label>
                                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Bubble.io" data-testid="provider-name" />
                                </div>
                                <div>
                                    <Label>Tipo</Label>
                                    <Select value={editing.provider_type} onValueChange={(v) => setEditing({ ...editing, provider_type: v })}>
                                        <SelectTrigger data-testid="provider-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PROVIDER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Client ID *</Label>
                                    <Input value={editing.client_id} onChange={(e) => setEditing({ ...editing, client_id: e.target.value })} data-testid="provider-client-id" />
                                </div>
                                <div>
                                    <Label>Client Secret {editing.id && '(dejar vacío para conservar)'}</Label>
                                    <Input type="password" value={editing.client_secret} onChange={(e) => setEditing({ ...editing, client_secret: e.target.value })} data-testid="provider-client-secret" />
                                </div>
                            </div>
                            <div>
                                <Label>Auth URL *</Label>
                                <Input value={editing.auth_url} onChange={(e) => setEditing({ ...editing, auth_url: e.target.value })} placeholder="https://.../oauth/authorize" data-testid="provider-auth-url" />
                            </div>
                            <div>
                                <Label>Token URL *</Label>
                                <Input value={editing.token_url} onChange={(e) => setEditing({ ...editing, token_url: e.target.value })} placeholder="https://.../oauth/access_token" data-testid="provider-token-url" />
                            </div>
                            <div>
                                <Label>Userinfo URL</Label>
                                <Input value={editing.userinfo_url} onChange={(e) => setEditing({ ...editing, userinfo_url: e.target.value })} placeholder="https://.../userinfo" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Scopes</Label>
                                    <Input value={editing.scopes} onChange={(e) => setEditing({ ...editing, scopes: e.target.value })} placeholder="openid profile email" />
                                </div>
                                <div>
                                    <Label>Redirect URI</Label>
                                    <Input value={editing.redirect_uri} onChange={(e) => setEditing({ ...editing, redirect_uri: e.target.value })} placeholder={defaultCallback} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <Label>Texto del botón</Label>
                                    <Input value={editing.button_label} onChange={(e) => setEditing({ ...editing, button_label: e.target.value })} placeholder="Ingresar con Bubble" />
                                </div>
                                <div>
                                    <Label>Color</Label>
                                    <Input type="color" value={editing.button_color} onChange={(e) => setEditing({ ...editing, button_color: e.target.value })} className="h-10 p-1" />
                                </div>
                            </div>
                            <div>
                                <Label>Instrucciones específicas</Label>
                                <Textarea value={editing.instructions} onChange={(e) => setEditing({ ...editing, instructions: e.target.value })} rows={4} placeholder="Pasos específicos para este proveedor..." />
                            </div>
                            <div className="flex items-center gap-2 py-2">
                                <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} id="enabled-switch" data-testid="provider-enabled" />
                                <Label htmlFor="enabled-switch">Habilitado</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                        <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="save-provider">{saving ? 'Guardando...' : 'Guardar'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Eliminar proveedor</DialogTitle>
                        <DialogDescription>¿Confirmas la eliminación? Esta acción no se puede deshacer.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={doDelete} data-testid="confirm-delete-provider">Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
